/**
 * Input Component
 * Accessible and responsive form input
 * 
 * Features:
 * - Proper touch target size (min 44px)
 * - Responsive text sizing
 * - Consistent styling
 * - Error states
 */

import { forwardRef, type InputHTMLAttributes } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  fullWidth?: boolean;
}

/**
 * Input Component
 * 
 * Provides a styled input with proper accessibility and responsive design
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error = false, fullWidth = true, className = '', ...props }, ref) => {
    const baseClasses = 'px-3 sm:px-4 py-2.5 sm:py-3 text-base border rounded-md transition-colors';
    const widthClass = fullWidth ? 'w-full' : '';
    const stateClasses = error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';
    const focusClasses = 'focus:ring-2 focus:outline-none';
    const disabledClasses = 'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed';
    
    // Ensure minimum touch target height of 44px
    const touchTargetClass = 'min-h-[44px]';

    return (
      <input
        ref={ref}
        className={`
          ${baseClasses}
          ${widthClass}
          ${stateClasses}
          ${focusClasses}
          ${disabledClasses}
          ${touchTargetClass}
          ${className}
        `}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

/**
 * Textarea Component
 * Accessible and responsive textarea
 */
interface TextareaProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  fullWidth?: boolean;
  rows?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ error = false, fullWidth = true, rows = 4, className = '', ...props }, ref) => {
    const baseClasses = 'px-3 sm:px-4 py-2.5 sm:py-3 text-base border rounded-md transition-colors resize-vertical';
    const widthClass = fullWidth ? 'w-full' : '';
    const stateClasses = error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';
    const focusClasses = 'focus:ring-2 focus:outline-none';
    const disabledClasses = 'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed';

    return (
      <textarea
        ref={ref}
        rows={rows}
        className={`
          ${baseClasses}
          ${widthClass}
          ${stateClasses}
          ${focusClasses}
          ${disabledClasses}
          ${className}
        `}
        {...props}
      />
    );
  }
);

Textarea.displayName = 'Textarea';

/**
 * Select Component
 * Accessible and responsive select dropdown with custom styling
 */
interface SelectProps extends InputHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ error = false, fullWidth = true, children, className = '', ...props }, ref) => {
    const baseClasses = 'px-3 sm:px-4 py-2.5 sm:py-3 pr-10 text-base border rounded-md transition-all appearance-none bg-white cursor-pointer';
    const widthClass = fullWidth ? 'w-full' : '';
    const stateClasses = error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500';
    const focusClasses = 'focus:ring-2 focus:outline-none';
    const disabledClasses = 'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed disabled:opacity-60';
    
    // Ensure minimum touch target height of 44px
    const touchTargetClass = 'min-h-[44px]';

    return (
      <div className="relative">
        <select
          ref={ref}
          className={`
            ${baseClasses}
            ${widthClass}
            ${stateClasses}
            ${focusClasses}
            ${disabledClasses}
            ${touchTargetClass}
            ${className}
          `}
          style={{
            backgroundImage: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
          }}
          {...props}
        >
          {children}
        </select>
        {/* Custom dropdown arrow */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <svg 
            className="h-5 w-5 text-gray-400" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    );
  }
);

Select.displayName = 'Select';
